import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import ui from "../../pages/dashboard/DashboardUI.module.css";
import styles from "./DetailsModal.module.css";

/**
 * DetailsModal - A read-only details view modal
 * 
 * Props:
 * - title: Modal title
 * - onClose: Callback when closing the modal
 * - onEdit: Callback when Edit button is clicked (if null, Edit button is hidden)
 * - onDelete: Callback when Delete button is clicked (if null, Delete button is hidden)
 * - children: Content to display in the modal body
 * - tabs: Optional array of tab objects { key, label } for tabbed content
 * - activeTab: Current active tab key (controlled)
 * - onTabChange: Callback when tab changes
 */
export default function DetailsModal({
  title,
  onClose,
  onEdit,
  onDelete,
  children,
  tabs = null,
  activeTab = null,
  onTabChange = null,
  deleteLabel = "Delete",
  editLabel = "Edit",
}) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return ReactDOM.createPortal(
    <div className={ui.modalOverlay}>
      <div className={`${ui.modal} ${styles.detailsModal}`}>
        {/* Header with tabs and close button */}
        <div className={styles.header}>
          {/* Tab Buttons */}
          {tabs && tabs.length > 0 ? (
            <div className={styles.tabContainer}>
              {tabs.map((tab) => {
                const tabKey = typeof tab === 'string' ? tab : tab.key;
                const tabLabel = typeof tab === 'string' ? tab : tab.label;
                
                return (
                  <button
                    key={tabKey}
                    type="button"
                    className={`${styles.tabButton} ${
                      activeTab === tabKey ? styles.activeTab : ""
                    }`}
                    onClick={() => onTabChange?.(tabKey)}
                  >
                    {tabLabel}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={styles.tabContainer} />
          )}
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className={styles.content}>
          {children}
        </div>

        {/* Footer Actions */}
        <div className={styles.footer}>
          <div className={styles.leftActions}>
            {onDelete && (
              <button
                type="button"
                className={ui.buttonDanger}
                onClick={onDelete}
              >
                {deleteLabel}
              </button>
            )}
          </div>
          <div className={styles.rightActions}>
            <button
              type="button"
              className={ui.cancelButton}
              onClick={onClose}
            >
              Close
            </button>
            {onEdit && (
              <button
                type="button"
                className={ui.buttonPrimary}
                onClick={onEdit}
              >
                {editLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
